import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { RADOM_COLORS, SANCTIONED_COUNTRIES, ZERO_POPULATION_TERRITORIES } from '../util/Constants'
import styled, { keyframes } from 'styled-components'
import { FadeOut } from '../components/Animations'
import { ManagedPaymentMethod, NetworkToChainMap, getMethod, projectId, wagmiConfig } from '../util/Managed'
import { PrimaryButton, SecondaryButton } from '../components/Button'
import { formatSecondsToMinutesAndSecondsString, TokenSelector } from '../components/PayWidget'
import _, { isEqual } from 'lodash'
import { InputLabel, TextInput, TextInputWithLabel, TextInputWithLabelWithSuggestions } from '../components/Input'
import RadomLogo from '../icons/Logo2'
import Dropdown from '../components/Dropdown'
import LoadingBar from '../components/LoadingBar'
import { pickTextColorBasedOnBgColor, shortAddress, splitDateTime } from '../util/Util'
import Radom, { IDonationLink, IManagedPaymentMethod, IManagedPaymentMethodDetails, IManagedPaymentStatus } from '../api/Radom'
import type { AddressSuggestionDetails } from '../api/Radom'
import { generateQRCode } from '../util/QRCode'
import CopyButton from '../components/CopyButton'
import { createWeb3Modal, useWeb3Modal } from '@web3modal/wagmi/react'
import { createWalletClient, custom, createPublicClient, http, getContract, erc20Abi, parseUnits, Address, parseEther } from 'viem'
import { useDisconnect, useAccount } from 'wagmi'
import { Container } from '../components/PayInvoice'
import { useLocation, useParams } from 'react-router-dom'
import { formatCurrency } from '../util/Currencies'
import { Country, ICountry } from 'country-state-city'
import KycView from './KycView'
import DateSelector from '../components/DateSelector'
import CountrySelector from '../components/CountrySelector'
import { CheckmarkWrapper } from '../components/Receipt'
import PendingSpinner from '../icons/PendingSpinner'
import SuccessSpinner from '../icons/SuccessSpinner'
import Crossmark from '../icons/Crossmark'
import { getSumSubErrorMessage } from '../util/Sumsub'
import { subYears } from 'date-fns'
import Checkbox from '../components/Checkbox'
import { trackCheckoutFunnelStep } from '../util/CheckoutFunnel'

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const AnimatedReceipt = styled.div`
  animation: ${fadeIn} 1s ease-in-out;
`

const BackgroundFadeIn = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: black;
  animation: ${FadeOut} 1s ease;
  opacity: 0;
`

const BackgroundScaleIn = keyframes`
  from {
    background-size: 110%;
  }
  to {
    background-size: 100%;
  }
`

const Background = styled.div`
  background-size: cover;
  height: 275px;
  max-height: 20%;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  position: relative;
  margin-bottom: 60px;
  background-repeat: no-repeat;
  animation: ${BackgroundScaleIn} 0.5s ease;
  background-position: top center;
`

const QRCodeWrapperOuter = styled.div`
  padding: 10px;
  border-radius: 20px;
  background-color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
`

const QRCodeWrapperInner = styled.div`
  width: 160px;
  height: 160px;
  position: relative;
  overflow: hidden;
  border-radius: 10px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

const PaymentDetailsContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 0;
  max-width: 100%;
`

const Timer = styled.div`
  font-size: 14px;
  font-color: #3464E6;
  font-weight: 600;
  color: ${RADOM_COLORS.NEW_BLUE};
  margin-bottom: 10px;
  background: #E7EDFF;
  padding: 8px 12px 8px 12px;
  gap: 8px;
  border-radius: 35px;
  opacity: 0px;
`

const IconContainer = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  margin-right: 4px;
  vertical-align: middle;
`

const PaymentDetailWithCopy = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`

const PaymentDetailText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 5px;
`

const PaymentDetailColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding-top: 15px;
`

const AmountInputShell = styled.div`
  position: relative;
`

const AmountPrefix = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #667085;
  font-size: 18px;
  font-weight: 500;
  pointer-events: none;
`

const getFiatCurrencySymbol = (currency: string): string => {
  try {
    const symbol = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol'
    }).formatToParts(0).find(part => part.type === 'currency')?.value

    return symbol ?? currency
  } catch {
    return currency
  }
}

const sanitizeFiatAmountInput = (rawValue: string): string => {
  const valueWithoutChars = rawValue.replace(/[^\d.]/g, '')
  const [whole = '', ...fractionParts] = valueWithoutChars.split('.')
  const fraction = fractionParts.join('').slice(0, 2)
  return fractionParts.length > 0 ? `${whole}.${fraction}` : whole
}

const normalizeFiatAmount = (rawValue: string): string => {
  if (!rawValue) {
    return ''
  }

  const parsed = Number.parseFloat(rawValue)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return ''
  }

  return parsed.toFixed(2)
}

export const ClickableLabel = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  cursor: pointer;
`

export const ExternalLinkText = styled.a`
  color: ${RADOM_COLORS.NEW_BLUE};
  align-items: center;
  display: flex;
  gap: 3px;
  text-decoration: none;

  &:hover {
    color: ${RADOM_COLORS.ORANGE};
    text-decoration: underline;
  }
`

interface kycData {
  name: string
  phoneNumber: string
  countryOfResidence: string
  address: string
  dateOfBirth: string
  passportFile?: string
  idCardFile?: string
  selfieFile?: string
  email: string
}

enum KycCheckingViewControl {
  NONE,
  PEDNING,
  APPROVED,
  SUBMITTED,
  REJECTED
}

function validEmail(email: string): boolean {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(email)
}

function composeAddressText(details: AddressSuggestionDetails | null, fallbackAddress: string): string {
  if (!details) {
    return fallbackAddress
  }

  const locality = [details.townOrCity, details.stateOrProvince]
    .map(part => part?.trim())
    .filter((part): part is string => Boolean(part && part.length > 0))
    .join(', ')

  const parts = [
    details.addressLine1,
    details.addressLine2,
    locality,
    details.postalCode
  ]
    .map(part => part?.trim())
    .filter((part): part is string => Boolean(part && part.length > 0))

  if (parts.length > 0) {
    return parts.join(', ')
  }

  return details.formattedAddress ?? fallbackAddress
}

function DonationLink(): ReactElement {
  const location = useLocation()
  const { donationLinkId: donationLinkIdParam } = useParams<{ donationLinkId: string }>()
  const isPreviewRoute = location.pathname === '/preview/donation'
  const donationLinkId = donationLinkIdParam ?? (isPreviewRoute ? 'previewdonation' : location.pathname.split('/').filter(Boolean).pop() ?? '')

  if (typeof window !== 'undefined' && !(window as any).__W3M_INITIALIZED__) {
    createWeb3Modal({ wagmiConfig, projectId, enableAnalytics: true })
    ;(window as any).__W3M_INITIALIZED__ = true
  }

  const [donationLink, setDonationLink] = useState<IDonationLink | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedMethod, setSelectedMethod] = useState<ManagedPaymentMethod | null>(null)
  const [donationAmount, setDonationAmount] = useState('')
  const [formatDonationAmount, setFormatDonationAmount] = useState('')
  const [closeFn, setCloseFn] = useState(() => () => { })
  const [paymentDetails, setPaymentDetails] = useState<IManagedPaymentMethodDetails | null>(null)
  const [managedPaymentQrCode, setManagedPaymentQrCode] = useState<string>('')
  const [formStep, setFormStep] = useState(1)
  const [isSubmitting] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState('')
  const [isPaymentConfirmed, setIsPaymentConfirmed] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState<IManagedPaymentStatus | null>(null)

  const { disconnect } = useDisconnect()
  const { address, isConnected, connector } = useAccount()
  const { open } = useWeb3Modal()
  const [isWalletLoading, setIsWalletLoading] = useState(false)
  const [checkTerms, setCheckTerms] = useState(false)

  const [errorMessage, setErrorMessage] = useState<string>()
  const errorRef = useRef<HTMLDivElement>(null)
  const trackedFunnelStepsRef = useRef<Set<string>>(new Set())
  const textColor = pickTextColorBasedOnBgColor(RADOM_COLORS.GRAY_LIGHTEST, 'rgba(255, 255, 255, 0.45)', 'rgba(0, 0, 0, 0.45)')

  const trackDonationStep = (
    step: 'viewed' | 'travelRuleCompleted' | 'paymentInitiated' | 'paymentConfirmed' | 'paymentFailed',
    metadata?: Record<string, unknown>
  ): void => {
    if (trackedFunnelStepsRef.current.has(step)) {
      return
    }

    trackedFunnelStepsRef.current.add(step)
    void trackCheckoutFunnelStep({
      sourceType: 'donationLink',
      sourceId: donationLink?.id ?? donationLinkId,
      rail: 'crypto',
      step,
      metadata
    })
  }

  const trackDonationPaymentInitiated = (method?: ManagedPaymentMethod | null): void => {
    trackDonationStep('paymentInitiated', {
      network: method?.tokenInfo?.networkName ?? method?.name,
      tokenAddress: method?.tokenInfo?.tokenAddress
    })
  }

  // kyc Stuff
  const [userKycData, setUserKycData] = useState<kycData>({
    name: '',
    phoneNumber: '',
    countryOfResidence: '',
    address: '',
    dateOfBirth: '',
    passportFile: '',
    idCardFile: '',
    email: '',
    selfieFile: ''
  })
  const kycIntervalRef = useRef<number | null>(null)
  const [kycViewControl, setKycViewControl] = useState<KycCheckingViewControl>(KycCheckingViewControl.NONE)
  const [addressSuggestionList, setAddressSuggestionList] = useState<string[]>([])
  const debouncedFetch = useRef(
    _.debounce((value: string, countryCode: string, setAddressSuggestionList) => {
      Radom.getGoolgePlaceAutoComplete(value, countryCode).then((res) => {
        setAddressSuggestionList(res)
      })
    }, 500)
  ).current

  useEffect(() => {
    if (donationLinkId !== 'previewdonation') {
      Radom.getDonationLink(donationLinkId)
        .then((data) => {
          setDonationLink(data)
          setIsLoading(false)
        })
        .catch((error) => {
          console.error(error)
          setIsLoading(false)
        })
    } else {
      const previewData = window.location.hash.substring(1)
      if (previewData) {
        const parsedData = JSON.parse(Buffer.from(previewData, 'base64').toString('utf8'))
        setDonationLink(parsedData)
      }
      setIsLoading(false)
    }
  }, [donationLinkId, window.location.hash])

  useEffect(() => {
    if (donationLink && donationLink.methods?.length > 0) {
      const method = donationLink.methods[0]
      setSelectedMethod(getMethod(method.network, method.token))
      trackDonationStep('viewed')
    } else {
      setSelectedMethod(null)
    }
  }, [donationLink])

  useEffect(() => {
    if (paymentDetails?.expiresAt) {
      const interval = setInterval(() => {
        const now = new Date().getTime()
        const distance = new Date(paymentDetails.expiresAt).getTime() - now

        setTimeRemaining(formatSecondsToMinutesAndSecondsString(
          (new Date(
            paymentDetails.expiresAt
          ).getTime() - new Date().getTime()) / 1000
        ))

        if (distance < 0) {
          clearInterval(interval)
          setTimeRemaining('EXPIRED')
        }
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [paymentDetails])

  useEffect(() => {
    if (kycIntervalRef.current) {
      clearTimeout(kycIntervalRef.current)
    }
    if (paymentDetails) {
      const checkPaymentInterval = setInterval(() => {
        Radom.getManagedPaymentStatus(paymentDetails.paymentId)
          .then(res => {
            if (res.status === 'paymentConfirmed') {
              trackDonationStep('paymentConfirmed')
              setPaymentStatus(res)
              setIsPaymentConfirmed(true)
              clearInterval(checkPaymentInterval)
            } else if (res.status === 'failed') {
              trackDonationStep('paymentFailed')
              clearInterval(checkPaymentInterval)
            }
          }).catch(() => { })
      }, 1000)

      return () => clearInterval(checkPaymentInterval)
    }
  }, [paymentDetails])

  useEffect(() => {
    if (kycViewControl === KycCheckingViewControl.SUBMITTED) {
      kycIntervalRef.current = window.setInterval(() => {
        Radom.getCustomerMatchEmailDobByDonationLink(
          donationLinkId,
          userKycData.email,
          userKycData.dateOfBirth
        )
          .then(async (res) => {
            if (res.kycCheck === 'Verified') {
              if (kycIntervalRef.current !== null) {
                clearInterval(kycIntervalRef.current)
                kycIntervalRef.current = null
              }

              if (!donationLink || !selectedMethod) return

              const paymentMethod = convertToIManagedPaymentMethod(selectedMethod)
              trackDonationPaymentInitiated(selectedMethod)
              const response = await Radom.createDonationLinkOrder({
                donationLinkId: donationLink.id,
                paymentMethod,
                orderData: [
                  {
                    key: 'email address',
                    value: userKycData.email.toLowerCase()
                  }
                ],
                amount: parseFloat(donationAmount),
                currency: donationLink.currency
              })

              if (response.paymentMethodDetails.managed) {
                setPaymentDetails(response.paymentMethodDetails.managed)
                generateManagedPaymentQrCode(response.paymentMethodDetails.managed)
                setKycViewControl(KycCheckingViewControl.APPROVED)

                setTimeout(() => {
                  setFormStep(4)
                  setKycViewControl(KycCheckingViewControl.NONE)
                }, 2000)
              } else {
                console.error('Payment method details are not available')
              }
            } else if (res.kycCheck === 'NotVerified') {
              if (kycIntervalRef.current !== null) {
                clearInterval(kycIntervalRef.current)
                kycIntervalRef.current = null
              }
              // setErrorMessage('KYC not verified')
              setKycViewControl(KycCheckingViewControl.REJECTED)
              setTimeout(() => {
                setKycViewControl(KycCheckingViewControl.NONE)
                setFormStep(1)
              }, 2000)
            }
          })
          .catch((e) => {
            if (kycIntervalRef.current !== null) {
              clearInterval(kycIntervalRef.current)
              kycIntervalRef.current = null
            }
            if (e.message === 'Customer not found') {
              setFormStep(2)
            } else {
              setErrorMessage(e.message)
            }
          })
      }, 2000)

      return () => {
        if (kycIntervalRef.current !== null) {
          clearInterval(kycIntervalRef.current)
          kycIntervalRef.current = null
        }
      }
    }
  }, [kycViewControl])
  if (!donationLink) {
    return <div>Error loading donation link</div>
  }

  const methods = donationLink.methods

  function convertToIManagedPaymentMethod(method: ManagedPaymentMethod): IManagedPaymentMethod {
    return {
      network: method.tokenInfo ? method.tokenInfo.networkName : method.name,
      token: method.tokenInfo ? method.tokenInfo.tokenAddress : undefined
    }
  }

  const handleNextStep = (): void => {
    setIsLoading(true)
    if (donationLinkId === 'previewdonation') {
      setIsLoading(false)
      return
    }

    setErrorMessage(undefined)

    if (!selectedMethod || !donationAmount) {
      setErrorMessage('Please select a payment method and enter a donation amount')
      return
    }

    const amount = parseFloat(donationAmount)
    if (isNaN(amount) || amount <= 0) {
      setErrorMessage('Please enter a valid donation amount')
      return
    }

    if (formStep === 1) {
      if (userKycData.email && userKycData.dateOfBirth) {
        if (!validEmail(userKycData.email)) {
          setErrorMessage('Please enter a valid email address')
          setIsLoading(false)
          return
        }
        Radom.getCustomerMatchEmailDobByDonationLink(donationLink.id, userKycData.email, userKycData.dateOfBirth)
          .then(async (res) => {
            if (res.kycCheck === 'Verified') {
              if (!selectedMethod) {
                return
              }
              const paymentMethod = convertToIManagedPaymentMethod(selectedMethod)
              trackDonationPaymentInitiated(selectedMethod)
              const response = await Radom.createDonationLinkOrder({
                donationLinkId: donationLink.id,
                paymentMethod,
                orderData: [
                  {
                    key: 'email address',
                    value: userKycData.email.toLowerCase()
                  }
                ],
                amount: parseFloat(donationAmount),
                currency: donationLink.currency
              })

              if (response.paymentMethodDetails.managed) {
                setPaymentDetails(response.paymentMethodDetails.managed)
                generateManagedPaymentQrCode(response.paymentMethodDetails.managed)
                setFormStep(4)
              } else {
                console.error('Payment method details are not available')
              }
            } else if (res.kycCheck === 'Submitted') {
              setKycViewControl(KycCheckingViewControl.SUBMITTED)
            } else if (res.failedReason !== '') {
              setKycViewControl(KycCheckingViewControl.NONE)
              if (res.failedReason !== null) {
                setErrorMessage(getSumSubErrorMessage(res.failedReason))
              }
              if (donationLink.customizations?.enableKycCheck === true &&
                 parseFloat(donationAmount) + parseFloat(res.total) >= 500) {
                setFormStep(2)
              } else {
                if (!selectedMethod) {
                  return
                }
                const paymentMethod = convertToIManagedPaymentMethod(selectedMethod)
                trackDonationPaymentInitiated(selectedMethod)
                const response = await Radom.createDonationLinkOrder({
                  donationLinkId: donationLink.id,
                  paymentMethod,
                  orderData: [
                    {
                      key: 'email address',
                      value: userKycData.email.toLowerCase()
                    }
                  ],
                  amount: parseFloat(donationAmount),
                  currency: donationLink.currency
                })
                if (response.paymentMethodDetails.managed) {
                  setPaymentDetails(response.paymentMethodDetails.managed)
                  generateManagedPaymentQrCode(response.paymentMethodDetails.managed)
                  setFormStep(4)
                } else {
                  console.error('Payment method details are not available')
                }
              }
            }
          })
          .catch((e) => {
            if (e.message === 'Customer not found') {
              setFormStep(2)
            } else {
              setErrorMessage(e.message)
            }
          })
          .finally(() => {
            setIsLoading(false)
          })
      }
    }

    if (formStep === 2) {
      let validationError: string | undefined
      if (!userKycData.name) {
        validationError = 'Please enter a valid name'
      }
      if (!userKycData.phoneNumber) {
        validationError = 'Please enter a valid phone number'
      }
      if (!userKycData.countryOfResidence) {
        validationError = 'Please select a valid country of residence'
      }
      if (!userKycData.address) {
        validationError = 'Please enter a valid address'
      }
      if (!userKycData.dateOfBirth) {
        validationError = 'Please enter a valid date of birth'
      }

      if (validationError) {
        setErrorMessage(validationError)
        setIsLoading(false)
        return
      }

      trackDonationStep('travelRuleCompleted')

      Radom.createCustomerForDonationLink(
        donationLinkId,
        {
          name: userKycData.name,
          phone: userKycData.phoneNumber,
          residenceCountry: userKycData.countryOfResidence,
          billingAddress: userKycData.address,
          dob: userKycData.dateOfBirth,
          passportFile: userKycData.passportFile,
          idCardFile: userKycData.idCardFile,
          selfieFile: userKycData.selfieFile,
          email: userKycData.email
        }
      ).then((res) => {
        if (donationLink.customizations?.enableKycCheck === true && parseFloat(donationAmount) >= 500) {
          setFormStep(3)
        } else {
          if (!selectedMethod) {
            return
          }
          const paymentMethod = convertToIManagedPaymentMethod(selectedMethod)
          trackDonationPaymentInitiated(selectedMethod)
          Radom.createDonationLinkOrder({
            donationLinkId: donationLink.id,
            paymentMethod,
            orderData: [
              {
                key: 'email address',
                value: userKycData.email.toLowerCase()
              }
            ],
            amount: parseFloat(donationAmount),
            currency: donationLink.currency
          })
            .then((response) => {
              if (response.paymentMethodDetails.managed) {
                setPaymentDetails(response.paymentMethodDetails.managed)
                generateManagedPaymentQrCode(response.paymentMethodDetails.managed)
                setFormStep(4)
              } else {
                console.error('Payment method details are not available')
              }
            })
            .catch((e) => {
              setErrorMessage(e.message)
            })
        }
      })
        .catch((e) => {
          if (e.message === 'Customer with this email already exists') {
            setFormStep(3)
          } else {
            setErrorMessage(e.message)
          }
        })
    }

    setIsLoading(false)
  }

  const generateManagedPaymentQrCode = async (
    details: IManagedPaymentMethodDetails
  ): Promise<void> => {
    if (!details.paymentAddress) {
      console.error('Payment address is not available')
      return
    }

    const [qrCodeData] = await generateQRCode(details)
    setManagedPaymentQrCode(qrCodeData)
  }

  const copyToClipboard = (text: string): void => {
    navigator.clipboard.writeText(text)
  }

  const onClickOpenWallet = async (): Promise<void> => {
    if (!paymentDetails) {
      return
    }

    switch (paymentDetails.network) {
    case 'Bitcoin':
    case 'BitcoinTestnet':
    case 'Dogecoin':
    case 'DogecoinTestnet':
    case 'Litecoin':
    case 'LitecoinTestnet':
    case 'BitcoinCash':
    case 'BitcoinCashTestnet':
    case 'BitcoinSV':
    case 'BitcoinSVTestnet':
    case 'Solana':
    case 'Polkadot':
    case 'PolkadotTestnet':
      break
    case 'Ethereum':
    case 'SepoliaTestnet':
    case 'Polygon':
    case 'PolygonTestnet':
    case 'BNB':
    case 'BNBTestnet':
    case 'Base':
    case 'Optimism':
    case 'OpBnb':
    case 'Arbitrum':
    case 'Avalanche':
      try {
        if (!address) {
          await open()
          return
        }

        if (!selectedMethod) {
          return
        }

        setIsWalletLoading(true)

        const chain = NetworkToChainMap[paymentDetails.network] as any
        const provider: any = await connector?.getProvider()

        const signer = createWalletClient({
          chain,
          transport: custom(provider)
        })

        if (connector?.switchChain) {
          const chainId = await connector.getChainId()
          if (chainId !== chain.id) {
            await connector?.switchChain({ chainId: chain.id })
          }
        }

        if (paymentDetails.token) {
          const publicClient = createPublicClient({
            chain,
            transport: http()
          })

          const contract = getContract({
            address: paymentDetails.token as any,
            abi: erc20Abi,
            client: { public: publicClient, wallet: signer }
          })

          const balance = await contract.read.balanceOf([address])
          const decimals = await contract.read.decimals()
          const parsedAmount = parseUnits(paymentDetails.amount.toString(), decimals)
          if (balance < parsedAmount) {
            setIsWalletLoading(false)
            return setErrorMessage(
              `Your wallet does not have enough ${getMethod(donationLink.methods[0].network, donationLink.methods[0].token).name} for payment.`
            )
          }

          await contract.write.transfer([
            paymentDetails.paymentAddress as Address,
            parseUnits(paymentDetails.amount, decimals)
          ], { account: address, chain })
        } else {
          const publicClient = createPublicClient({
            chain,
            transport: http()
          })

          try {
            const balance = await publicClient.getBalance({ address })
            if (balance < parseEther(paymentDetails.amount)) {
              setIsWalletLoading(false)
              return setErrorMessage(`Your wallet does not have enough ${selectedMethod?.ticker ?? 'amount'} for payment.`)
            }
          } catch (err) {
            console.error(err)
          }

          if (!signer) return

          await signer.sendTransaction({
            account: address,
            chain,
            to: paymentDetails.paymentAddress as Address,
            value: parseEther(paymentDetails.amount)
          })

          setIsWalletLoading(false)
        }
      } catch (err) {
        setIsWalletLoading(false)
        console.error(err)
      }
      break
    default:
      console.error('No wallet')
    }
  }

  // The list of countries must not include:
  // * Sanctioned countries
  // * Territories with zero population
  const allowedCountryCodes = donationLink.customizations?.allowedCountries

  const baseCountries = Country.getAllCountries().filter(
    c => !SANCTIONED_COUNTRIES.has(c.name) && !ZERO_POPULATION_TERRITORIES.has(c.name)
  )

  const allCountries: ICountry[] = allowedCountryCodes
    ? baseCountries.filter(c => allowedCountryCodes.includes(c.isoCode))
    : baseCountries

  if (isPaymentConfirmed && paymentStatus) {
    const transaction = paymentStatus.transactions[0]

    return (
      <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'white', zIndex: 5, position: 'relative' }}>
        <AnimatedReceipt style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', width: '450px', margin: '20px auto', color: 'black', zIndex: 10 }}>
          <div style={{ marginBottom: '10px', backgroundImage: `url(${donationLink.imageUrl})`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center', height: '150px', width: '150px', backgroundColor: 'white', borderRadius: '100%', border: '8px solid rgba(255, 255, 255, 1)', boxShadow: '0 0 10px rgba(0, 0, 0, 0.15)', zIndex: 1 }} />
          <span style={{ fontSize: '16px', fontWeight: '700', marginBottom: '40px' }}>Thank you for supporting {donationLink.name ?? 'Unknown'}</span>

          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: '2.5px' }}>
            <span style={{ fontSize: '12px', fontWeight: '300', wordBreak: 'break-all', textAlign: 'left' }}><span style={{ fontWeight: 400, fontSize: '14px' }}>Donation Amount:</span> {paymentDetails?.amount} {paymentDetails?.token ?? paymentDetails?.network}</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: '2.5px' }}>
            <span style={{ fontSize: '12px', fontWeight: '300', wordBreak: 'break-all', textAlign: 'left' }}><span style={{ fontWeight: 400, fontSize: '14px' }}>Payment Address:</span> {shortAddress(paymentDetails?.paymentAddress ?? '0x', 15)}</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: '300', wordBreak: 'break-all', textAlign: 'left' }}><span style={{ fontWeight: 400, fontSize: '14px' }}>Tx Hash:</span> {shortAddress(transaction.transactionHash ?? '0x', 20)}</span>
            <CopyButton onClick={() => copyToClipboard(transaction.transactionHash)}>Copy</CopyButton>
          </div>
        </AnimatedReceipt>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'white', fontSize: 14, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Background style={{ backgroundImage: `url(${donationLink.bannerImageUrl})`, backgroundColor: RADOM_COLORS.GRAY_MED }}>
          <div style={{
            backgroundImage: `url(${donationLink.imageUrl})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            height: 150,
            width: 150,
            marginBottom: -60,
            backgroundColor: 'white',
            borderRadius: '100%',
            border: '8px solid rgba(255, 255, 255, 1)',
            boxShadow: '0 0 10px rgba(0, 0, 0, 0.15)',
            zIndex: 1
          }} />
          <BackgroundFadeIn />
        </Background>
        {
          isLoading &&
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999
            }}
          >
            <CheckmarkWrapper style={{ background: RADOM_COLORS.NEW_BLUE, width: 50, height: 50, opacity: 0.8 }}>
              <PendingSpinner animationDelay='0.5s' animationSpeed='0.75s' stroke={'white'} />
            </CheckmarkWrapper>
          </div>
        }
        {
          donationLink.customizations?.warningMessage && <>
            <div style={{
              position: 'fixed',
              top: 0,
              paddingTop: 10,
              paddingBottom: 10,
              paddingLeft: 10,
              paddingRight: 10,
              textAlign: 'center',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              backgroundColor: '#71afe5',
              color: 'white',
              zIndex: 99
            }}>
              {donationLink.customizations.warningMessage}
            </div>
          </>
        }
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20, gap: 40 }}>
          {formStep < 3 && (<div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', fontSize: 20 }}>
            <span style={{ color: RADOM_COLORS.GRAY_DARKEST }}>Donate with crypto to</span>
            <a href='#' style={{ fontWeight: 500, textDecoration: 'underline', color: RADOM_COLORS.BLACK }}>{donationLink.name ?? 'Unknown'}</a>
          </div>)}

          <div style={{ width: 'fit-content', maxWidth: '90%', display: 'flex', flexDirection: 'column', gap: 15 }}>

            {
              errorMessage && errorMessage.length > 0 &&
              <div ref={errorRef} style={{
                backgroundColor: RADOM_COLORS.ERROR,
                padding: 15,
                borderRadius: 5,
                marginBottom: 20,
                color: 'white',
                overflowWrap: 'break-word'
              }}>
                {errorMessage}
              </div>
            }

            {
              kycViewControl === KycCheckingViewControl.NONE && <>

                {formStep === 1 && (
                  <Container style={{ margin: 'initial', width: 450, padding: 0, maxWidth: '100%' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <InputLabel>Select donation method</InputLabel>
                      {methods?.length > 0
                        ? (
                          <Dropdown
                            outerStyle={{
                              background: 'white',
                              border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                              borderRadius: 5
                            }}
                            noChevron={methods.length <= 1}
                            onCloseFn={fn => setCloseFn(() => fn)}
                            selectedContent={
                              <>
                                {selectedMethod && (
                                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                    <div style={{ width: 25, height: 25, backgroundImage: `url(${selectedMethod.logo})`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', position: 'relative' }}>
                                      {selectedMethod.tokenInfo && (
                                        <div style={{ background: 'rgb(255, 255, 255)', borderRadius: '100%', width: 15, height: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'absolute', bottom: -1, right: -5, boxShadow: '0 0 1px' }}>
                                          <div style={{ width: 10, height: 10, backgroundImage: `url(${selectedMethod.tokenInfo.logo})`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
                                        </div>
                                      )}
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                                        <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>{selectedMethod.ticker}</span>
                                        <span style={{ color: RADOM_COLORS.GRAY_DARKEST }}>
                                          on {selectedMethod.tokenInfo?.networkName ?? selectedMethod.name}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            }
                            dropdownContent={
                              <>
                                {methods
                                  .map(method => getMethod(method.network, method.token))
                                  .filter(method => !isEqual(selectedMethod, method))
                                  .map((method, i) => {
                                    return (
                                      <TokenSelector
                                        hoverEffect
                                        key={i}
                                        isDisabled={false}
                                        isSelected={false}
                                        style={{ opacity: 1, border: 'none' }}
                                        onClick={() => {
                                          setSelectedMethod(method)
                                          closeFn()
                                        }}
                                      >
                                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                          <div style={{ width: 25, height: 25, backgroundImage: `url(${method.logo})`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', position: 'relative' }}>
                                            {method.tokenInfo && (
                                              <div style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '100%', width: 15, height: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'absolute', bottom: -1, right: -5, boxShadow: '0 0 1px' }}>
                                                <div style={{ width: 10, height: 10, backgroundImage: `url(${method.tokenInfo.logo})`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
                                              </div>
                                            )}
                                          </div>
                                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                                              <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>{method.ticker}</span>
                                              <span style={{ color: RADOM_COLORS.GRAY_DARKEST }}>
                                                on {method.tokenInfo?.networkName ?? method.name}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </TokenSelector>
                                    )
                                  })}

                              </>
                            }
                          />
                        )
                        : (
                          <LoadingBar style={{ width: '100%', height: 40 }} />
                        )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <InputLabel>Enter donation amount</InputLabel>
                      {donationLink
                        ? (
                          selectedMethod
                            ? (
                              <AmountInputShell>
                                <AmountPrefix>{getFiatCurrencySymbol(donationLink.currency)}</AmountPrefix>
                                <TextInput
                                  style={{
                                    height: 48,
                                    paddingLeft: 36,
                                    fontSize: 20,
                                    lineHeight: 1.2,
                                    fontWeight: 600
                                  }}
                                  disabled={false}
                                  autoFocus
                                  placeholder="0.00"
                                  min="0"
                                  inputMode="decimal"
                                  autoComplete="off"
                                  value={formatDonationAmount}
                                  onFocus={(e) => {
                                    setFormatDonationAmount(donationAmount)
                                    e.currentTarget.select()
                                  }}
                                  onBlur={() => {
                                    const normalized = normalizeFiatAmount(donationAmount)
                                    setDonationAmount(normalized)
                                    setFormatDonationAmount(normalized)
                                  }}
                                  onChange={(e) => {
                                    const value = sanitizeFiatAmountInput(e.target.value)
                                    setDonationAmount(value)
                                    setFormatDonationAmount(value)
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault()
                                      e.currentTarget.blur()
                                    }
                                  }}
                                />
                              </AmountInputShell>
                            )
                            : (
                              <div style={{ fontSize: 14, color: RADOM_COLORS.GRAY_DARKER, borderRadius: 5, border: `1px solid ${RADOM_COLORS.GRAY_DARK}`, padding: '10px 15px' }}>
                                No available payment methods
                              </div>
                            )
                        )
                        : (
                          <LoadingBar />
                        )}
                      <div style={{ display: 'flex', gap: 5 }}>
                        {[25, 50, 100, 250, 500].map((a, i) =>
                          donationLink
                            ? (
                              <SecondaryButton
                                onClick={() => {
                                  const formatted = Number(a).toFixed(2)
                                  setDonationAmount(formatted)
                                  setFormatDonationAmount(formatted)
                                }}
                                key={i}
                                style={{
                                  padding: 10,
                                  borderRadius: 5,
                                  border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                                  flexGrow: 1,
                                  display: 'flex',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  color: RADOM_COLORS.BLACK
                                }}>
                                {formatCurrency(donationLink.currency, a)}
                              </SecondaryButton>
                            )
                            : (
                              <LoadingBar key={i} style={{ width: '100%', height: 40 }} />
                            )
                        )}
                      </div>
                    </div>
                    <TextInputWithLabel
                      label={'Email address'}
                      value={userKycData.email}
                      required={true}
                      onChange={(e) => setUserKycData({ ...userKycData, email: (e.target as HTMLInputElement).value })}
                      placeholder='Email address'
                      autoFocus
                      inputStyle={{
                        background: 'white',
                        border: `1px solid ${RADOM_COLORS.GRAY_DARK}`
                      }}
                    />

                    <div style={{ fontSize: 14, display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                      <InputLabel>
                        <span>Date of birth</span>
                      </InputLabel>
                      <DateSelector
                        innerStyle={{
                          background: 'white',
                          border: `1px solid ${RADOM_COLORS.GRAY_DARK}`
                        }}
                        placeholder='DD/MM/YYYY'
                        value={userKycData.dateOfBirth ? new Date(userKycData.dateOfBirth) : undefined}
                        onChange={(e) =>
                          setUserKycData({
                            ...userKycData,
                            dateOfBirth: e ? splitDateTime(e).datePart : ''
                          })
                        }
                        startDate={new Date('1935-01-01')}
                        endDate={subYears(new Date(), 18)}
                      />
                    </div>
                    <ClickableLabel
                      onClick={() => setCheckTerms(!checkTerms)}
                      style={{
                        marginLeft: 5,
                        display: 'flex',
                        alignItems: 'flex-start', // 确保所有元素从顶部对齐
                        gap: 8 // 在元素之间添加间距
                      }}>
                      <div>
                        <Checkbox checked={checkTerms}
                        />
                      </div>
                      {
                        donationLink.tosUrl && <div
                          style={{
                            color: RADOM_COLORS.GRAY_DARKEST,
                            fontSize: 12
                          }}>
                          I accept the <span
                            onClick={() => {
                              window.open(donationLink.tosUrl, '_blank')
                            }}
                            style={{
                              color: RADOM_COLORS.NEW_BLUE, textDecoration: 'underline', cursor: 'pointer'
                            }}>Terms of Service</span> of the organization receiving my donation.
                          This donation is processed by Radom. By continuing, you also agree to our <span
                            onClick={(e) => {
                              e.preventDefault()
                              window.open('https://radom.com/terms', '_blank')
                            }}
                            style={{
                              color: RADOM_COLORS.NEW_BLUE, textDecoration: 'underline', cursor: 'pointer'
                            }}
                          >terms</span> and <span
                            onClick={(e) => {
                              e.preventDefault()
                              window.open('https://radom.com/privacy', '_blank')
                            }}
                            style={{
                              color: RADOM_COLORS.NEW_BLUE, textDecoration: 'underline', cursor: 'pointer'
                            }}
                          >privacy policy</span>.
                        </div>
                      }
                      {
                        !donationLink.tosUrl && <div
                          style={{
                            color: RADOM_COLORS.GRAY_DARKEST,
                            fontSize: 12
                          }}>
                          This donation is processed by Radom. By continuing, you agree to our <span
                            onClick={(e) => {
                              e.preventDefault()
                              window.open('https://radom.com/terms', '_blank')
                            }}
                            style={{
                              color: RADOM_COLORS.NEW_BLUE, textDecoration: 'underline', cursor: 'pointer'
                            }}
                          >terms</span> and <span
                            onClick={(e) => {
                              e.preventDefault()
                              window.open('https://radom.com/privacy', '_blank')
                            }}
                            style={{
                              color: RADOM_COLORS.NEW_BLUE, textDecoration: 'underline', cursor: 'pointer'
                            }}
                          >privacy policy</span>.
                        </div>
                      }
                    </ClickableLabel>

                    <PrimaryButton
                      disabled={
                        !userKycData.email ||
                        !userKycData.dateOfBirth ||
                        !donationAmount ||
                        !selectedMethod ||
                        !checkTerms}
                      onClick={handleNextStep}
                      style={{ background: donationLink.primaryButtonColor, fontSize: 16, height: 50, marginTop: '20px' }}
                    >
                      Next
                    </PrimaryButton>
                  </Container>
                )}

                {formStep === 2 && (
                  <div
                    style={{
                      display: 'flex',
                      position: 'relative',
                      flexDirection: 'column',
                      gap: 10,
                      width: 450,
                      maxWidth: '100%',
                      padding: 0
                    }}
                  >
                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: 14, display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                          <TextInputWithLabel
                            inputStyle={{
                              background: 'white',
                              border: `1px solid ${RADOM_COLORS.GRAY_DARK}`
                            }}
                            label={'Full Name'}
                            value={userKycData.name}
                            required={true}
                            onChange={(e) => setUserKycData({
                              ...userKycData,
                              name: (e.target as HTMLInputElement).value
                            })}
                          />
                        </div>

                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                        <InputLabel>{'Country of Residence'}</InputLabel>
                        <CountrySelector
                          allowedCountries={allCountries}
                          selectedCountry={
                            (
                              allCountries.find(
                                c =>
                                  c.name.toLowerCase() === userKycData.countryOfResidence.toLowerCase() ||
                                  c.isoCode.toLowerCase() === userKycData.countryOfResidence.toLowerCase()
                              )?.name ?? userKycData.countryOfResidence
                            ) || ''
                          }
                          onCountrySelected={country => {
                            if (country) {
                              setUserKycData(prev => ({
                                ...prev,
                                phoneNumber: prev.phoneNumber.trim().length === 0
                                  ? `+${country.phonecode} `
                                  : prev.phoneNumber,
                                countryOfResidence: country.name
                              }))
                            }
                          }}
                        />
                      </div>
                      {/* <input className="pac-container" /> */}
                      <TextInputWithLabelWithSuggestions
                        label={'Address'}
                        value={userKycData.address}
                        required={true}
                        suggestionList={addressSuggestionList}
                        inputStyle={{
                          background: 'white',
                          border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                          cursor: 'text'
                        }}
                        setSelectedItems={(items) => {
                          const selectedAddress = items[0]
                          if (!selectedAddress) {
                            return
                          }
                          setAddressSuggestionList([])

                          setUserKycData(prev => ({
                            ...prev,
                            address: selectedAddress
                          }))

                          void (async () => {
                            let details: AddressSuggestionDetails | null = null
                            try {
                              const selectedCountry = allCountries.find(c => c.name === userKycData.countryOfResidence)
                              details = await Radom.getGooglePlaceAddressDetails(
                                selectedAddress,
                                selectedCountry?.isoCode ?? ''
                              )
                            } catch (err) {
                              console.error('Failed to resolve selected address details', err)
                              details = null
                            }

                            if (!details) {
                              return
                            }

                            const countryFromDetails = details.country?.toLowerCase()
                            const matchedCountry = allCountries.find(
                              c => c.name.toLowerCase() === countryFromDetails ||
                                c.isoCode.toLowerCase() === countryFromDetails
                            )

                            setUserKycData(prev => ({
                              ...prev,
                              address: composeAddressText(details, selectedAddress),
                              countryOfResidence:
                                prev.countryOfResidence.trim().length > 0
                                  ? prev.countryOfResidence
                                  : (matchedCountry?.name ?? prev.countryOfResidence),
                              phoneNumber: (matchedCountry && prev.phoneNumber.trim().length === 0)
                                ? `+${matchedCountry.phonecode} `
                                : prev.phoneNumber
                            }))
                          })()
                        }}
                        onChange={(e) => {
                          const value = (e.target as HTMLInputElement).value
                          if (value.length === 0) {
                            setAddressSuggestionList([])
                          }

                          setUserKycData(prev => ({
                            ...prev,
                            address: value
                          }))

                          if (value.length > 2) {
                            const country = allCountries.find(
                              (c) => c.name === userKycData.countryOfResidence
                            )
                            debouncedFetch(value, country?.isoCode ?? '', setAddressSuggestionList)
                          } else {
                            setAddressSuggestionList([])
                          }
                        }}
                      />

                      <TextInputWithLabel
                        label={'Phone Number'}
                        required
                        inputStyle={{
                          background: 'white',
                          border: `1px solid ${RADOM_COLORS.GRAY_DARK}`
                        }}
                        value={userKycData.phoneNumber}
                        onChange={(e) => setUserKycData({
                          ...userKycData,
                          phoneNumber: (e.target as HTMLInputElement).value
                        })}
                      />
                    </div>

                    <PrimaryButton
                      onClick={handleNextStep}
                      isLoading={isSubmitting}
                      disabled={!userKycData.name ||
                        !userKycData.phoneNumber || !userKycData.countryOfResidence || !userKycData.address}
                      style={{
                        background: donationLink.primaryButtonColor,
                        fontSize: 16,
                        height: 50,
                        marginTop: 20
                      }}>
                      Next
                    </PrimaryButton>
                  </div>
                )}
                {formStep === 3 && (
                  <>
                    <KycView
                      errorMessage={errorMessage}
                      minHeight={200}
                      onBack={() => {
                        setFormStep(2)
                        setErrorMessage('')
                      }}
                      onChange={async (value) => {
                        setIsLoading(true)
                        if (value.kycType === 2) {
                          if (value.documentType === 'passport') {
                            setUserKycData({
                              ...userKycData,
                              passportFile: value.imageFile
                            })
                            await Radom.updateCustomerKycDataByDonationLink(
                              donationLinkId,
                              userKycData.email,
                              userKycData.dateOfBirth,
                              {
                                name: userKycData.name,
                                phone: userKycData.phoneNumber,
                                address: userKycData.address,
                                dateOfBirth: new Date(userKycData.dateOfBirth).toLocaleDateString('en-CA'),
                                residenceCountry: userKycData.countryOfResidence,
                                passportFile: value.imageFile
                              }
                            )
                              .then(() => {
                                setErrorMessage('')
                              })
                              .catch((err) => {
                                console.error(err)
                                setErrorMessage(err.message)
                              }
                              )
                              .finally(() => {
                                setIsLoading(false)
                              })
                          } else if (value.documentType === 'id_card') {
                            setUserKycData({
                              ...userKycData,
                              idCardFile: value.imageFile
                            })
                            await Radom.updateCustomerKycDataByDonationLink(
                              donationLinkId,
                              userKycData.email,
                              userKycData.dateOfBirth,
                              {
                                name: userKycData.name,
                                phone: userKycData.phoneNumber,
                                address: userKycData.address,
                                dateOfBirth: new Date(userKycData.dateOfBirth).toLocaleDateString('en-CA'),
                                residenceCountry: userKycData.countryOfResidence,
                                idCardFile: value.imageFile
                              }
                            )
                              .then(() => {
                                setErrorMessage('')
                              })
                              .catch((err) => {
                                console.error(err)
                                setErrorMessage(err.message)
                              }
                              )
                              .finally(() => {
                                setIsLoading(false)
                              })
                          }
                        } else if (value.kycType === 3) {
                          await Radom.updateCustomerKycDataByDonationLink(
                            donationLinkId,
                            userKycData.email,
                            userKycData.dateOfBirth,
                            {
                              name: userKycData.name,
                              phone: userKycData.phoneNumber,
                              address: userKycData.address,
                              dateOfBirth: new Date(userKycData.dateOfBirth).toLocaleDateString('en-CA'),
                              residenceCountry: userKycData.countryOfResidence,
                              selfieFile: value.imageFile
                            })
                            .then(() => {
                              setErrorMessage('')
                            })
                            .catch((err) => {
                              console.error(err)
                              setErrorMessage(err.message)
                            })
                            .finally(() => {
                              setIsLoading(false)
                            })
                          setUserKycData({
                            ...userKycData,
                            selfieFile: value.imageFile
                          })
                        }
                      }}
                      onSubmit={async () => {
                        setIsLoading(true)
                        setKycViewControl(KycCheckingViewControl.PEDNING)
                        await Radom.submitCustomerKycCheckRequestByDonationLink(
                          donationLinkId,
                          userKycData.email,
                          userKycData.dateOfBirth
                        )
                          .then(() => {
                            setTimeout(() => {
                              setKycViewControl(KycCheckingViewControl.SUBMITTED)
                            }, 1000)
                          })
                          .catch((err) => {
                            console.error(err)
                            setKycViewControl(KycCheckingViewControl.NONE)
                            setErrorMessage(err.message)
                          })
                          .finally(() => {
                            setIsLoading(false)
                          })
                      }
                      }
                    />
                  </>
                )}

                {formStep === 4 && (
                  <PaymentDetailsContainer>
                    <span style={{ color: '#747894', fontSize: '18px', fontWeight: '600' }}>
                      Donating <IconContainer style={{ backgroundImage: `url(${selectedMethod?.logo})` }} />
                      <span style={{ color: '#33324F' }}>{selectedMethod?.name}</span> on <IconContainer style={{ backgroundImage: `url(${selectedMethod?.tokenInfo?.logo ?? selectedMethod?.logo})` }} />
                      <span style={{ color: '#33324F' }}>{selectedMethod?.tokenInfo?.networkName ?? selectedMethod?.name}</span> network
                    </span>
                    <span style={{ color: '#555470', fontSize: '12px', fontWeight: '400', textAlign: 'center', paddingBottom: '10px' }}>
                      Connect to your wallet, scan the QR code or copy the {selectedMethod?.name} address to
                      {' '}make a payment.
                    </span>
                    <QRCodeWrapperOuter>
                      <QRCodeWrapperInner>
                        <img src={managedPaymentQrCode} alt="QR Code" />
                      </QRCodeWrapperInner>
                    </QRCodeWrapperOuter>
                    <PaymentDetailColumn>
                      <Timer><span style={{ fontWeight: '500', color: '#618AF9', fontSize: '14px', marginRight: '2px' }}>Time Remaining</span> {timeRemaining}</Timer>
                    </PaymentDetailColumn>
                    <PaymentDetailWithCopy>
                      <PaymentDetailText>
                        <div style={{ color: '#33324F', fontSize: '12px', fontWeight: '500', marginBottom: '5px' }}>Payment Address</div>
                        <div style={{ color: '#747894', fontSize: '12px', fontWeight: '400' }}>{paymentDetails?.paymentAddress}</div>
                      </PaymentDetailText>
                      <CopyButton onClick={() => copyToClipboard(paymentDetails?.paymentAddress ?? '')}>Copy</CopyButton>
                    </PaymentDetailWithCopy>
                    <PaymentDetailWithCopy>
                      <PaymentDetailText>
                        <div style={{ color: '#33324F', fontSize: '12px', fontWeight: '500', marginBottom: '5px' }}>Total Amount</div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <IconContainer style={{ backgroundImage: `url(${selectedMethod?.logo})` }} />
                          <span style={{ color: '#000000', fontSize: '16px', fontWeight: '500' }}>{paymentDetails?.amount} <span style={{ fontWeight: '400', color: '#555470' }}>{selectedMethod?.ticker}</span></span>
                        </div>
                      </PaymentDetailText>
                      <CopyButton onClick={() => copyToClipboard(paymentDetails?.amount ?? '')}>Copy</CopyButton>
                    </PaymentDetailWithCopy>

                    {
                      selectedMethod?.isWalletSupported &&
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        backgroundColor: address ? RADOM_COLORS.GRAY_LIGHTEST : 'initial',
                        borderBottomLeftRadius: 25,
                        borderBottomRightRadius: 25,
                        borderTopLeftRadius: 5,
                        borderTopRightRadius: 5
                      }}>
                        {
                          address &&
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 10 }}>
                            <div style={{ color: RADOM_COLORS.GRAY_DARKEST, display: 'flex', flexDirection: 'column', gap: 5 }}>
                              <span style={{ textTransform: 'uppercase', fontSize: 10 }}>Connected wallet</span>
                              <span>{shortAddress(address, 10)}</span>
                            </div>
                            <SecondaryButton
                              disabled={isWalletLoading}
                              onClick={() => disconnect()}
                              style={{ padding: 0, border: 0, color: RADOM_COLORS.GRAY_DARKEST, textTransform: 'uppercase', fontSize: 10 }}>
                              Disconnect
                            </SecondaryButton>
                          </div>
                        }
                        <PrimaryButton
                          onClick={onClickOpenWallet}
                          isLoading={isWalletLoading}
                          spinnerColor={'white'}
                          style={{ background: donationLink.primaryButtonColor, color: 'white', width: '100%' }}>
                          {isConnected ? 'Approve payment' : 'Connect wallet'}
                        </PrimaryButton>
                      </div>
                    }
                  </PaymentDetailsContainer>
                )}
              </>
            }

            {
              kycViewControl === KycCheckingViewControl.PEDNING && <div
                style={{
                  height: 400,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  gap: 10
                }}

              >
                <CheckmarkWrapper style={{ background: RADOM_COLORS.NEW_BLUE, width: 50, height: 50 }}>
                  <PendingSpinner animationDelay='0.5s' animationSpeed='0.75s' stroke={'white'} />
                </CheckmarkWrapper>
              </div>
            }

            {
              kycViewControl === KycCheckingViewControl.APPROVED && <div
                style={{
                  height: 400,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  gap: 10
                }}

              >
                <CheckmarkWrapper style={{ background: RADOM_COLORS.SUCCESS, width: 50, height: 50 }}>
                  <SuccessSpinner animationDelay='0.5s' animationSpeed='0.75s' stroke={'white'} />
                </CheckmarkWrapper>
                <div>
                  Your KYC is approved.
                </div>
              </div>
            }
            {
              kycViewControl === KycCheckingViewControl.SUBMITTED && <div
                style={{
                  height: 400,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  gap: 20
                }}

              >
                <CheckmarkWrapper style={{ background: RADOM_COLORS.SUCCESS, width: 50, height: 50 }}>
                  <PendingSpinner animationDelay='0.5s' animationSpeed='0.75s' stroke={'white'} />
                </CheckmarkWrapper>
                <div>
                  Your details are being verified.
                </div>
              </div>
            }
            {
              kycViewControl === KycCheckingViewControl.REJECTED && <div
                style={{
                  height: 400,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  gap: 10
                }}

              >
                <CheckmarkWrapper style={{ background: RADOM_COLORS.ERROR, width: 45, height: 45 }}>
                  <Crossmark animationDelay='0.5s' animationSpeed='0.75s' stroke={'white'} />
                </CheckmarkWrapper>
                <div>
                  Your KYC is rejected. Please try again.
                </div>
              </div>
            }
          </div>
          <a href='https://radom.com'
            target='_blank'
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              userSelect: 'none',
              fontSize: 14,
              color: 'rgba(0, 0, 0, 0.45)',
              fontWeight: 100,
              textDecoration: 'none',
              padding: 20,
              justifySelf: 'flex-end'
            }}
            rel="noreferrer">
            <span>Powered by</span>
            <RadomLogo width={60} height={14} fill={textColor} />
          </a>
        </div>

      </div>
    </div>
  )
}

export default DonationLink
